import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon } from '@fortawesome/free-solid-svg-icons';
import Colors from '../../../themes/Colors';
import {
  isDarkTheme,
  setTheme,
  THEME_DARK,
  THEME_LIGHT,
  THEME_CHANGE_EVENT
} from '../../../themes/theme';

const ThemeSwitchHolder = styled.div`
  align-items: center;
  background-color: ${Colors.surfaceMuted};
  border-radius: 8px;
  display: inline-flex;
  flex-shrink: 0;
  gap: 2px;
  height: 34px;
  padding: 4px;

  .themeOption {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: ${Colors.textMuted};
    cursor: pointer;
    display: flex;
    font-size: 14px;
    height: 26px;
    justify-content: center;
    padding: 0;
    transition: background-color 0.2s, color 0.2s;
    width: 32px;
  }
  .themeOption:hover {
    color: ${Colors.textStrong};
  }
  .themeOption:focus-visible {
    outline: 2px solid ${Colors.primaryAccent};
    outline-offset: 1px;
  }
  .themeOption.active {
    background-color: ${Colors.snow};
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.15);
    color: ${Colors.textStrong};
  }
  .themeOption.light.active {
    color: ${Colors.atention};
  }
`;

/*
  Segmented control claro / oscuro (sol / luna) del topbar. Persiste en
  localStorage (ver themes/theme.js) y aplica el tema al instante, sin recargar.
  Escucha THEME_CHANGE_EVENT, así que varias instancias (topbar y drawer
  mobile) quedan sincronizadas.
*/
const ThemeSwitch = ({ className = '' }) => {
  const { t } = useTranslation();
  const [dark, setDark] = useState(isDarkTheme());

  useEffect(() => {
    const onChange = e => setDark(e.detail === THEME_DARK);
    window.addEventListener(THEME_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, onChange);
  }, []);

  const handleSelect = nextDark => {
    if (nextDark === dark) return;
    setDark(nextDark);
    setTheme(nextDark ? THEME_DARK : THEME_LIGHT);
  };

  const options = [
    { isDark: false, icon: faSun, label: t('Light mode') },
    { isDark: true, icon: faMoon, label: t('Dark mode') }
  ];

  return (
    <ThemeSwitchHolder
      className={`themeSwitch ${className}`}
      role="radiogroup"
      aria-label={t('Theme')}
      onClick={e => e.stopPropagation()}
    >
      {options.map(({ isDark, icon, label }) => (
        <button
          key={label}
          type="button"
          role="radio"
          aria-checked={dark === isDark}
          aria-label={label}
          title={label}
          className={`themeOption ${isDark ? 'dark' : 'light'}${dark === isDark ? ' active' : ''}`}
          onClick={() => handleSelect(isDark)}
        >
          <FontAwesomeIcon icon={icon} />
        </button>
      ))}
    </ThemeSwitchHolder>
  );
};

export default ThemeSwitch;
